<template>
  <transition name="drawer">
    <div v-if="modelValue" :class="style.overlay" @click.self="close">
      <div :class="style.drawer">
        <div :class="style.header">
          <h3>Заявка</h3>
          <ButtonIcon
            :icon="closeIcon"
            @click="close"
            size="28"
            variant="ghost"
          />
        </div>

        <div :class="style.content">
          <section :class="style.section">
            <AccordionBase text="Контакты" :isHideActions="false">
              <div :class="style.fieldsContainer">
                <div :class="style.field">
                  <span>Дата и время</span>
                  <p>{{ getDate(application) }}</p>
                </div>

                <div :class="style.field">
                  <span>Устройство</span>
                  <div :class="style.deviceBlock">
                    <IconBase
                      :icon="
                        isMobileApplication(application)
                          ? mobileIcon
                          : desktopIcon
                      "
                      tone="tertiary"
                      :class="style.deviceIcon"
                    />
                    <p>{{ getDevice(application) }}</p>
                  </div>
                </div>

                <div :class="style.field">
                  <span>Имя</span>
                  <p>{{ getValue(application, "ФИО") || "-" }}</p>
                </div>

                <div :class="style.field">
                  <span>Телефон</span>
                  <p>{{ getValue(application, "Телефон") || "-" }}</p>
                </div>
              </div>
            </AccordionBase>
          </section>

          <section :class="style.section">
            <AccordionBase
              text="Ответы"
              :defaultOpen="false"
              :isHideActions="false"
            >
              <div :class="style.fieldsContainer">
                <div
                  v-for="(val, key) in answers"
                  :key="key"
                  :class="style.field"
                >
                  <span>{{ key }}</span>
                  <p>{{ val }}</p>
                </div>
              </div>
            </AccordionBase>
          </section>

          <section :class="style.section">
            <AccordionBase
              text="Ссылки"
              :defaultOpen="false"
              :isHideActions="false"
            >
              <div :class="style.fieldsContainer">
                <div v-if="application.URL" :class="style.field">
                  <span>URL</span>
                  <p>{{ application.URL }}</p>
                </div>

                <div v-if="application.Utm" :class="style.field">
                  <span>UTM</span>
                  <p>{{ application.Utm }}</p>
                </div>
              </div>
            </AccordionBase>
          </section>

          <section :class="style.section">
            <AccordionBase
              text="Интеграции"
              :defaultOpen="false"
              :isHideActions="false"
            >
              <div :class="style.fieldsContainer">
                <div
                  v-for="integration in integrations"
                  :key="integration.key"
                  :class="style.integration"
                >
                  <div :class="style.integrationBlock">
                    <img :src="integration.icon" alt="" />
                    <span>{{ integration.label }}</span>
                  </div>
                  <div
                    :class="style.errorBlock"
                    v-if="integration.text === 'Ошибка'"
                  >
                    <span>Ошибка</span>
                    <img :src="warningIcon" alt="" />
                    <ButtonIcon
                      v-if="integration.key === 'telegram'"
                      :icon="resetIcon"
                      :disabled="retryingTelegram"
                      title="Повторно отправить в Telegram"
                      @click.stop="retryTelegram"
                    />
                    <button
                      v-if="integration.key === 'amo'"
                      :class="style.retryAction"
                      :disabled="retryingAmo"
                      title="Повторно отправить в AmoCRM"
                      @click.stop="retryAmo"
                    >
                      <img :src="resetIcon" alt="" />
                      <span>{{ retryingAmo ? "Отправка" : "Повторить" }}</span>
                    </button>
                    <ButtonIcon
                      v-if="integration.key === 'bitrix'"
                      :icon="resetIcon"
                      :disabled="retryingBitrix"
                      @click.stop="retryBitrix"
                    />
                  </div>
                </div>
              </div>
            </AccordionBase>
          </section>

          <section :class="style.section">
            <div :class="style.deleteButtonField" style="gap: 12px">
              <span :class="style.headerSection">Действия</span>
              <SecondaryButton
                :class="style.deleteButton"
                text="Удалить заявку"
                :icon="trashIcon"
                @click="$emit('delete', application['_id'])"
              />
            </div>
          </section>
        </div>
      </div>
    </div>
  </transition>
</template>

<script lang="ts">
import {defineComponent, PropType} from "vue";
import {API} from "@/api/generated";
import style from "./ApplicationDetailsDrawer.module.scss";
import closeIcon from "$images/icons/button/Name=Close, Size=16, Tone=Default.svg";
import AccordionBase from "@/elements/UI/accordion/AccordionBase.vue";
import SecondaryButton from "@/elements/UI/button/ButtonStyle/Default/secondary/SecondaryButton.vue";
import trashIcon from "$images/icons/button/Name=Trash, Size=16, Tone=Default.svg";
import mobileIcon from "$images/icons/tabs/Name=Mobile, Size=16.svg";
import desktopIcon from "$images/icons/tabs/Name=Desktop, Size=16.svg";

import {
  ApplicationAK,
  strObj
} from "@/page/onlineAppointment/application/ApplicationType";
import mailIcon from "$images/Mail.svg";
import tgIcon from "$images/TG.svg";
import AmoIcon from "$images/Amo.svg";
import bitrixIcon from "$images/Site.svg";
import googleIcon from "$images/google.svg";
import warningIcon from "$images/icons/badge/Name=Warning, Size=M.svg";
import resetIcon from "$images/icons/button/Name=Reset, Size=16, Tone=Default.svg";
import IconBase from "@/elements/UI/icon/IconBase.vue";
import ButtonIcon from "@/elements/UI/button/ButtonIcon/ButtonIcon.vue";

type IntegrationView = {
  key: string;
  label: string;
  text: string;
  icon: string;
};

export default defineComponent({
  name: "ApplicationDetailsDrawer",
  components: {
    ButtonIcon,
    IconBase,
    SecondaryButton,
    AccordionBase
  },
  props: {
    modelValue: Boolean,
    application: {
      type: Object as PropType<ApplicationAK>,
      default: () => ({})
    },
    connectedIntegrations: {
      type: Array as PropType<string[]>,
      default: () => []
    }
  },
  emits: ["update:modelValue", "delete"],
  data() {
    return {
      style,
      closeIcon,
      trashIcon,
      warningIcon,
      mobileIcon,
      desktopIcon,
      resetIcon,
      retryingTelegram: false,
      retryingAmo: false,
      retryingBitrix: false
    };
  },
  methods: {
    close() {
      this.$emit("update:modelValue", false);
    },

    async retryTelegram() {
      const id = this.application?._id;
      if (!id || this.retryingTelegram) return;

      this.retryingTelegram = true;
      try {
        const response = await API.resendTelegram({body: {id}});
        const telegramStatus =
          response?.telegramm || response?.statusSend?.telegramm;

        if (telegramStatus) {
          if (!this.application.statusSend) {
            this.application.statusSend = {} as NonNullable<
              ApplicationAK["statusSend"]
            >;
          }
          this.application.statusSend.telegramm = telegramStatus;
        }
      } catch (error) {
        console.error(
          "Не удалось повторно отправить заявку в Telegram:",
          error
        );
      } finally {
        this.retryingTelegram = false;
      }
    },

    async retryAmo() {
      const id = this.application?._id;
      if (!id || this.retryingAmo) return;

      this.retryingAmo = true;
      try {
        const response = await API.resendAmo({body: {id}});
        const amoStatus = response?.amo || response?.statusSend?.amo;
        if (amoStatus) {
          if (!this.application.statusSend) {
            this.application.statusSend = {} as NonNullable<
              ApplicationAK["statusSend"]
            >;
          }
          this.application.statusSend.amo = amoStatus;
        }
      } catch (error) {
        console.error("Не удалось повторно отправить заявку в AmoCRM:", error);
      } finally {
        this.retryingAmo = false;
      }
    },

    async retryBitrix() {
      const id = this.application?._id;
      if (!id || this.retryingBitrix) return;

      this.retryingBitrix = true;
      try {
        const response = await API.resendQuizDataToBitrix({body: {id}});
        if (this.application.statusSend && response?.bitrix) {
          this.application.statusSend.bitrix = response.bitrix;
        }
      } catch (error) {
        console.error(
          "Не удалось повторно отправить заявку в Bitrix24:",
          error
        );
      } finally {
        this.retryingBitrix = false;
      }
    },

    getValue(obj: ApplicationAK, key: string) {
      if (!obj) return "";

      if (obj[key]) {
        return obj[key] as string | strObj;
      }

      switch (key) {
        case "Телефон":
          return obj["ClientPhone"] || "";
        case "ФИО":
          return obj["ClientFullName"] ?? (obj["Имя"] as string) ?? "";
        default:
          return "";
      }
    },

    getDate(obj: ApplicationAK) {
      return obj?.Дата || obj?.DateTime || obj?.createdAt || "-";
    },

    getDevice(obj: ApplicationAK) {
      const device = obj?.Device || obj?.deviceType;

      if (device === "Компьютер") return "ПК";
      if (device === "Телефон") return "МБ";

      return device || "-";
    },

    getMailStatus() {
      console.log(this.application?.statusSend?.mail, "STATUS");
      return this.application?.statusSend?.mail;
    },

    getTelegramStatus() {
      console.log(this.application?.statusSend?.telegramm, "STATUS");
      return this.application?.statusSend?.telegramm;
    },

    getAmoStatus() {
      console.log(this.application?.statusSend?.amo, "STATUS");
      return this.application?.statusSend?.amo;
    },

    getBitrixStatus() {
      return this.application?.statusSend?.bitrix;
    },

    getGoogleStatus() {
      console.log(this.application?.statusSend?.google, "STATUS");
      return this.application?.statusSend?.google;
    },

    isSystemKey(key: string) {
      const excludedKeys = new Set([
        "ФИО",
        "Телефон",
        "Имя",
        "ClientPhone",
        "ClientFullName",
        "Device",
        "deviceType",
        "Дата",
        "DateTime",
        "createdAt",
        "updatedAt",
        "URL",
        "Url",
        "url",
        "Utm",
        "UTM",
        "utm",
        "statusMail",
        "statusTg",
        "statusAmo",
        "statusGoogle",
        "statusSend",
        "_id",
        "__v",
        "id"
      ]);

      return excludedKeys.has(key);
    },

    resolveObjectStatus(value: Record<string, unknown> | undefined) {
      if (!value || typeof value !== "object") {
        return {
          text: "Нет данных"
        };
      }

      const values = Object.values(value);

      if (!values.length) {
        return {
          text: "Нет данных"
        };
      }

      const allOk = values.every((item) => item === "ok");
      const allNotOk = values.every((item) => item !== "ok");

      if (allOk) {
        return {
          text: "OK"
        };
      }

      if (allNotOk) {
        return {
          text: "Ошибка"
        };
      }

      return {
        text: "Частично"
      };
    },

    resolveAmoStatus(value: Record<string, unknown> | undefined) {
      if (!value || typeof value !== "object") {
        return {
          text: "Нет данных"
        };
      }

      const goodStatus = ["описание добавлено к заявке", "заявка создана"];

      const statusList = Object.values(value).filter(
        (item): item is string => typeof item === "string"
      );

      if (!statusList.length) {
        return {
          text: "Нет данных"
        };
      }

      const successCount = statusList.filter((status) =>
        goodStatus.includes(status)
      ).length;

      if (successCount === 0) {
        return {
          text: "Ошибка"
        };
      }

      if (successCount === statusList.length) {
        return {
          text: "OK"
        };
      }

      return {
        text: "Частично"
      };
    },

    resolveBitrixStatus(value: Record<string, unknown> | undefined) {
      if (!value || typeof value !== "object") {
        return {
          text: "Нет данных"
        };
      }

      const statusList = Object.values(value).filter(
        (item): item is string => typeof item === "string"
      );

      if (!statusList.length) {
        return {
          text: "Нет данных"
        };
      }

      const successCount = statusList.filter((status) =>
        status.toLowerCase().startsWith("лид создан")
      ).length;

      if (successCount === 0) {
        return {
          text: "Ошибка"
        };
      }

      if (successCount === statusList.length) {
        return {
          text: "OK"
        };
      }

      return {
        text: "Частично"
      };
    },

    resolveGoogleStatus(value: unknown) {
      const statusMessages = this.getGoogleStatusMessages(value);

      if (!statusMessages.length) {
        return {
          text: "Нет данных"
        };
      }

      if (this.isGoogleStatusWithoutIntegration(statusMessages)) {
        return {
          text: "Нет данных"
        };
      }

      const successTexts = [
        "Добавленны новые данные в таблицу",
        "Добавлены новые данные в таблицу"
      ];
      const isSuccess = statusMessages.some((message) =>
        successTexts.some((successText) => message.includes(successText))
      );

      return isSuccess ? {text: "OK"} : {text: "Ошибка"};
    },
    getGoogleStatusMessages(value: unknown): string[] {
      if (typeof value === "string") {
        return value.trim() ? [value] : [];
      }

      if (Array.isArray(value)) {
        return value
          .filter((item): item is string => typeof item === "string")
          .map((item) => item.trim())
          .filter(Boolean);
      }

      return [];
    },
    isGoogleStatusWithoutIntegration(statusMessages: string[]): boolean {
      const withoutIntegrationPatterns = [
        "ошибка получения данных из google sheets",
        "не подключ",
        "не настро",
        "не найд",
        "not connected",
        "not configured",
        "not found",
        "no google",
        "no spreadsheet",
        "no integration"
      ];

      return statusMessages.every((message) => {
        const normalizedMessage = message.toLowerCase();
        return withoutIntegrationPatterns.some((pattern) =>
          normalizedMessage.includes(pattern)
        );
      });
    },
    isMobileApplication(application: ApplicationAK): boolean {
      return this.getDevice(application) !== "ПК";
    }
  },
  computed: {
    answers(): Record<string, string> {
      if (!this.application) return {};

      return Object.fromEntries(
        Object.entries(this.application).filter(
          ([key, val]) =>
            !this.isSystemKey(key) &&
            typeof val === "string" &&
            val.trim() !== ""
        )
      ) as Record<string, string>;
    },

    integrations(): IntegrationView[] {
      const mail = this.resolveObjectStatus(this.getMailStatus());
      const telegram = this.resolveObjectStatus(this.getTelegramStatus());
      const amo = this.resolveAmoStatus(this.getAmoStatus());
      const bitrix = this.resolveBitrixStatus(this.getBitrixStatus());
      const google = this.resolveGoogleStatus(this.getGoogleStatus());

      return [
        {
          key: "mail",
          label: "Email",
          text: mail.text,
          icon: mailIcon
        },
        {
          key: "telegram",
          label: "Telegram",
          text: telegram.text,
          icon: tgIcon
        },
        {
          key: "amo",
          label: "AmoCRM",
          text: amo.text,
          icon: AmoIcon
        },
        {
          key: "bitrix",
          label: "Bitrix24",
          text: bitrix.text,
          icon: bitrixIcon
        },
        {
          key: "google",
          label: "Google Таблицы",
          text: google.text,
          icon: googleIcon
        }
      ].filter((integration) =>
        this.connectedIntegrations.includes(integration.key)
      );
    }
  }
});
</script>
